import React from 'react';
import { Helmet } from 'react-helmet-async';

interface HeadProps {
  title: string;
  description: string;
  image?: string;
  imageWidth?: string;
  imageHeight?: string;
  imageType?: string;
  type?: string;
  url?: string;
  canonicalUrl?: string;
  keywords?: string | string[];
  schemaOverride?: Record<string, any>;
  noIndex?: boolean;
}

/**
 * Component for managing document head metadata using react-helmet-async.
 * Handles title, meta tags, and JSON-LD structured data.
 */
export const Head: React.FC<HeadProps> = ({
  title,
  description,
  image = '/assets/ad_campaign/kivosystems_preview.png',
  imageWidth = '1200',
  imageHeight = '630',
  imageType = 'image/png',
  type = 'website',
  url,
  canonicalUrl,
  keywords,
  schemaOverride,
  noIndex = false
}) => {
  const siteUrl = 'https://www.kivosystems.com';
  const fullCanonical = canonicalUrl ? (canonicalUrl.startsWith('http') ? canonicalUrl : `${siteUrl}${canonicalUrl}`) : (url ? url : siteUrl);

  const resolveImageUrl = (imageUrl?: string) => {
    if (!imageUrl) return `${siteUrl}/logo.svg`;
    if (/^https?:\/\//i.test(imageUrl)) return imageUrl;
    const normalizedPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return `${siteUrl}${normalizedPath}`;
  };

  const fullOgImage = resolveImageUrl(image);

  const keywordString = Array.isArray(keywords) ? keywords.join(', ') : keywords;

  // Default schema
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Kivosystems",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.svg`,
    "description": "Cyprus + London based consultancy. We design and build professional websites and digital tools.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "London",
      "addressCountry": "UK"
    },
    "sameAs": [
      "https://www.linkedin.com/company/kivosystems"
    ]
  };

  const jsonLd = schemaOverride || defaultSchema;

  return (
    <Helmet>
      {/* Basic Metadata */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywordString && <meta name="keywords" content={keywordString} />}
      <link rel="canonical" href={fullCanonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />
      <meta property="og:image:type" content={imageType} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />

      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};
